import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminEmailRoute from "@/components/AdminEmailRoute";
import AdminLayout from "@/components/AdminLayout";
import AdminOnlyRoute from "@/components/AdminOnlyRoute";
import AppLayout from "@/components/AppLayout";
import MarketingLayout from "@/components/MarketingLayout";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

const Landing = lazyWithRetry(() => import("./pages/Landing"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Company = lazyWithRetry(() => import("./pages/Company"));
const Partners = lazyWithRetry(() => import("./pages/Partners"));
const Faq = lazyWithRetry(() => import("./pages/Faq"));
const Guide = lazyWithRetry(() => import("./pages/Guide"));
const Charity = lazyWithRetry(() => import("./pages/Charity"));
const ClubGrant = lazyWithRetry(() => import("./pages/ClubGrant"));
const InvoiceRequest = lazyWithRetry(() => import("./pages/InvoiceRequest"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const DemoLayout = lazyWithRetry(() => import("./components/DemoLayout"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const Register = lazyWithRetry(() => import("./pages/Register"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const UpdatePassword = lazyWithRetry(() => import("./pages/UpdatePassword"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const AdminLogin = lazyWithRetry(() => import("./pages/AdminLogin"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Trips = lazyWithRetry(() => import("./pages/Trips"));
const NewTrip = lazyWithRetry(() => import("./pages/NewTrip"));
const TripDetail = lazyWithRetry(() => import("./pages/TripDetail"));
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const Demo = lazyWithRetry(() => import("./pages/Demo"));
const ManagePage = lazyWithRetry(() => import("./pages/ManagePage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const More = lazyWithRetry(() => import("./pages/More"));
const Help = lazyWithRetry(() => import("./pages/Help"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const AdminStripeAudit = lazyWithRetry(() => import("./pages/AdminStripeAudit"));
const AdminMaster = lazyWithRetry(() => import("./pages/AdminMaster"));
const AdminPartnerNew = lazyWithRetry(() => import("./pages/AdminPartnerNew"));
const AdminPartners = lazyWithRetry(() => import("./pages/AdminPartners"));
const AdminPartnerDetail = lazyWithRetry(() => import("./pages/AdminPartnerDetail"));
const AdminPartnerPayouts = lazyWithRetry(() => import("./pages/AdminPartnerPayouts"));
const AdminPartnerBulk = lazyWithRetry(() => import("./pages/AdminPartnerBulk"));

const queryClient = new QueryClient();
const RouteFallback = (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const AuthRouteGroup = () => (
  <AuthProvider>
    <Outlet />
  </AuthProvider>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ErrorBoundary>
            <Suspense fallback={RouteFallback}>
              <Routes>
                {/* Marketing routes with shared layout */}
                <Route element={<MarketingLayout />}>
                  <Route path="/" element={<Landing />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/company" element={<Company />} />
                  <Route path="/kumppanit" element={<Partners />} />
                  <Route path="/seurat" element={<ClubGrant />} />
                  <Route path="/ukk" element={<Faq />} />
                  <Route path="/ohjeet" element={<Guide />} />
                  <Route path="/hyvantekevaisyys" element={<Charity />} />
                  <Route path="/verkkolasku" element={<InvoiceRequest />} />
                  <Route path="/blogi" element={<Blog />} />
                  <Route path="/blogi/:slug" element={<BlogPost />} />
                </Route>

                {/* Public demo routes without auth provider */}
                <Route element={<DemoLayout />}>
                  <Route path="/demo" element={<Demo />} />
                </Route>

                {/* Auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/update-password" element={<UpdatePassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/admin/login" element={<AdminLogin />} />

                {/* App routes with auth provider */}
                <Route element={<AuthRouteGroup />}>
                  <Route
                    element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/trips" element={<Trips />} />
                    <Route path="/trips/new" element={<NewTrip />} />
                    <Route path="/trips/:id" element={<TripDetail />} />
                    <Route path="/trips/:id/edit" element={<NewTrip />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/manage" element={<ManagePage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/more" element={<More />} />
                    <Route path="/help" element={<Help />} />
                  </Route>

                  {/* Admin routes (separate layout) */}
                  <Route
                    element={
                      <AdminOnlyRoute>
                        <AdminLayout />
                      </AdminOnlyRoute>
                    }
                  >
                    <Route path="/admin/stripe-audit" element={<AdminStripeAudit />} />
                    <Route path="/admin" element={<AdminMaster />} />
                    <Route path="/admin/partners" element={<AdminEmailRoute><AdminPartners /></AdminEmailRoute>} />
                    <Route path="/admin/partners/payouts" element={<AdminEmailRoute><AdminPartnerPayouts /></AdminEmailRoute>} />
                    <Route path="/admin/partners/new" element={<AdminEmailRoute><AdminPartnerNew /></AdminEmailRoute>} />
                    <Route path="/admin/partners/bulk" element={<AdminEmailRoute><AdminPartnerBulk /></AdminEmailRoute>} />
                    <Route path="/admin/partners/:id" element={<AdminEmailRoute><AdminPartnerDetail /></AdminEmailRoute>} />
                  </Route>
                </Route>

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <CookieConsent />
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
